import memoize from 'memoize-one';
import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';

import { getAssessmentOnlineCourseChapters } from 'my-core/assessment-utils';
import { isStdTestCourse } from 'my-core/course-utils';
import CourseContext from 'my-core/CourseContext';
import { trackCustomEvent } from 'my-core/gtm-events';
import { useCurrentUser, useSearchParams, useStandardApiRequest, useTitle } from 'my-core/hooks';
import getOnlineCourseContentMap, { findChild, getNext } from 'my-core/online-course-content-map';
import { getUserSubmissionSectionResults } from 'my-core/online-course-practice-mode-utils';
import {
  courseLandingPath,
  studentCoursePageAssessmentRelativePath,
  studentCoursePageOnDemandRelativePath,
} from 'my-core/routes';
import { getIsStudySessionExpired, sortStudySessions } from 'my-core/study-session-utils';
import {
  getIsActiveOrTrialing,
  useCurrentUserProductSubscription,
  useCurrentUserSubscription,
} from 'my-core/subscription-utils';

import { findAssessmentWithContents } from './student-course-page-utils';
import StudentCoursePage from './StudentCoursePage';

import { fetchCourseApi } from 'my-actions/CourseActions';

export default function StudentCoursePageCtnr() {
  const { idOrSlug } = useParams();
  const currentUser = useCurrentUser();
  const subscription = useCurrentUserSubscription();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const courseFetchKey = `student_course_page_fetch_course_${idOrSlug}`;
  const { performRequest: performFetch, requestStatus: fetchStatus } = useStandardApiRequest({
    actionCreator: fetchCourseApi,
    requestKey: courseFetchKey,
  });
  useEffect(() => {
    if (!fetchStatus) {
      performFetch(idOrSlug, COURSE_FETCH_PARAMS, courseFetchKey);
    }
  }, [courseFetchKey, fetchStatus, idOrSlug, performFetch]);

  const course = useSelector(state =>
    getCourse(idOrSlug, state.apiRequests[courseFetchKey]?.id, state.courses.items, state.schools.items),
  );

  useEffect(() => {
    if (!fetchStatus?.success) return;
    trackCustomEvent(
      'In-Course-Experience Viewed',
      {
        course_code: course.code,
        course_id: course.id,
        course_slug: course.slug,
        school_name: course.school?.name,
        school_id: course.school_id,
        is_high_priority: course.high_priority,
        has_class_rep: course.has_class_rep,
      },
      { custIO: true, intercom: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStatus]);

  const userCourse = useSelector(state =>
    getUserCourse(course?.current_user_user_course_id, course?.id, currentUser.id, state.userCourses.items),
  );
  const courseSubscription = useCurrentUserProductSubscription(course?.stripe_product_id);

  const classRepCourse = useSelector(state =>
    getClassRepCourse(
      course?.current_user_class_rep_course_id,
      course?.id,
      currentUser.id,
      state.classRepCourses.items,
    ),
  );

  const courseInstructor = useSelector(state =>
    getCourseInstructor(course?.active_course_instructor_ids, state.courseInstructors.items, state.users.items),
  );

  const studySessions = useSelector(state =>
    getStudySessions(
      currentUser.id,
      course?.id,
      getIsActiveOrTrialing(subscription),
      state.courses.items[state.apiRequests[courseFetchKey]?.id]?.public_study_session_ids, // course?.public_study_session_ids,
      state.courses.items[state.apiRequests[courseFetchKey]?.id]?.current_user_extended_access_study_session_ids,
      state.studySessions.items,
      state.studySessionUsers.items,
      state.users.items,
      state.courseAssessments.items,
      state.courses.items,
    ),
  );

  const studySessionBundles = useSelector(state =>
    getStudySessionBundles(
      state.courses.items[state.apiRequests[courseFetchKey]?.id]?.public_study_session_bundle_ids,
      state.studySessionBundles.items,
      state.studySessionBundleUsers.items,
      state.studySessions.items,
    ),
  );

  const [classPass, onlineCourses] = useSelector(state =>
    getOnlineCourses(
      state.courses.items[state.apiRequests[courseFetchKey]?.id]?.public_online_course_ids,
      currentUser.id,
      state.onlineCourses.items,
      state.users.items,
      state.onlineCourseUsers.items,
    ),
  );

  const assessments = useSelector(state =>
    getAssessmentsWithContents(
      state.courses.items[state.apiRequests[courseFetchKey]?.id]?.public_assessment_ids,
      state.courseAssessments.items,
      classPass,
    ),
  );
  const classPassContentMap = useMemo(() => classPass && getOnlineCourseContentMap(classPass), [classPass]);
  const flashCardDecks = useSelector(state =>
    getFlashCardDecks(course?.public_flash_card_deck_ids, state.flashCardDecks.items),
  );

  // PRACTICE MODE
  const practiceModeSectionResults = useSelector(state =>
    getPracticeModeSectionResults(
      classPass?.current_user_practice_mode_results,
      state.quizQuestionSubmissions.items,
      currentUser.id,
    ),
  );

  useTitle(`${course?.code} at ${course?.school?.short_name || course?.school?.name}`);

  const navigate = useNavigate();
  const redirect = getRedirect();
  // if (redirect) return <Navigate replace to={redirect} />;
  useEffect(() => {
    if (redirect) navigate(redirect, { replace: true });
  }, [navigate, redirect]);

  if (isStdTestCourse(course)) {
    return <Navigate replace to={courseLandingPath(course, !!userCourse)} />;
  }

  return (
    <CourseContext value={course?.slug}>
      <StudentCoursePage
        key={idOrSlug}
        assessments={assessments}
        classPass={classPass}
        classPassContentMap={classPassContentMap}
        classRepCourse={classRepCourse}
        course={course}
        courseInstructor={courseInstructor}
        courseSubscription={courseSubscription}
        currentUser={currentUser}
        fetchStatus={fetchStatus}
        flashCardDecks={flashCardDecks}
        idOrSlug={idOrSlug}
        onlineCourses={onlineCourses}
        practiceModeSectionResults={practiceModeSectionResults}
        studySessionBundles={studySessionBundles}
        studySessions={studySessions}
        subscription={subscription}
        userCourse={userCourse}
      />
    </CourseContext>
  );

  function getRedirect() {
    if (searchParams.resume) {
      const ocu = classPass?.current_user_online_course_user;
      const currentProg = ocu?.progress?.current;
      const assessment = findAssessmentWithContents(assessments, currentProg?.assessment_id);
      if (!(assessment || classPassContentMap)) return;
      const currentNode =
        (currentProg?.activity_id &&
          findChild(
            assessment?.contents || classPassContentMap,
            n => n.type === 'activity' && n.data.id === currentProg.activity_id,
          )) ||
        findChild(assessment?.contents || classPassContentMap, n => n.type === 'activity');
      return (
        currentNode &&
        `${assessment ? studentCoursePageAssessmentRelativePath(assessment) : studentCoursePageOnDemandRelativePath()}${currentNode.fullPath}`
      );
    }
    if (searchParams.activity_id && searchParams.activity_type) {
      const activityType = searchParams.activity_type;
      const activityId = +searchParams.activity_id;
      const { assessment, node } = findMatchingNode(
        n => n.type === 'activity' && n.data.activity_type === activityType && n.data.activity_id === activityId,
      );
      if (!node) return;
      return {
        hash: location.hash,
        pathname: `${assessment ? studentCoursePageAssessmentRelativePath(assessment) : studentCoursePageOnDemandRelativePath()}${node.fullPath}`,
        search: queryString.stringify({ ...searchParams, activity_id: undefined, activity_type: undefined }),
      };
    }
    if (searchParams.sect_id) {
      const sectId = +searchParams.sect_id;

      const { assessment, node } = findMatchingNode(n => n.type === 'section' && n.data.id === sectId);
      if (!node) return;
      return {
        hash: location.hash,
        pathname: `${assessment ? studentCoursePageAssessmentRelativePath(assessment) : studentCoursePageOnDemandRelativePath()}${searchParams.practice_mode ? '/practice-mode' : ''}${node.fullPath}`,
        search: queryString.stringify({ ...searchParams, sect_id: undefined, practice_mode: undefined }),
      };
    }
    if (searchParams.chap_id) {
      const chapId = +searchParams.chap_id;
      const assessmentId = +searchParams.assessment_id;
      let assessment, chapNode;
      if (assessmentId) {
        assessment = assessments.find(a => a.id === assessmentId);
        if (assessment?.contents)
          chapNode = findChild(assessment.contents, n => n.type === 'chapter' && n.data.id === chapId);
      }
      if (!chapNode) {
        const assessmentAndNode = findMatchingNode(n => n.type === 'chapter' && n.data.id === chapId);
        assessment = assessmentAndNode.assessment;
        chapNode = assessmentAndNode.node;
      }
      if (chapNode) {
        const act = chapNode && getNext(chapNode, n => n.type === 'activity');
        return (
          act &&
          `${assessment ? studentCoursePageAssessmentRelativePath(assessment) : studentCoursePageOnDemandRelativePath()}${act.fullPath}`
        );
      }
    }
  }

  function findMatchingNode(finder) {
    for (const assessment of assessments) {
      if (!assessment.contents) continue;
      const node = findChild(assessment.contents, finder);
      if (node) return { assessment, node };
    }
    if (classPassContentMap) {
      const node = findChild(classPassContentMap, finder);
      if (node) return { node };
    }
    return {};
  }
}

const getCourse = memoize((idOrSlug, id, courses, schools) => {
  const course = courses[idOrSlug] || courses[id] || Object.values(courses).find(c => c.slug === idOrSlug);
  return course && { ...course, school: schools[course.school_id] };
});
const getUserCourse = memoize(
  (ucId, courseId, userId, userCourses) =>
    userCourses[ucId] ||
    (courseId && userId && Object.values(userCourses).find(uc => uc.course_id === courseId && uc.user_id === userId)),
);
const getClassRepCourse = memoize(
  (crcId, courseId, userId, classRepCourses) =>
    classRepCourses[crcId] ||
    (courseId &&
      userId &&
      Object.values(classRepCourses).find(crc => crc.course_id === courseId && crc.user_id === userId)),
);
const getCourseInstructor = memoize((ciIds = [], courseInstructors, users) => {
  const ci = courseInstructors[ciIds[0]];
  return ci && { ...ci, instructor: users[ci.instructor_id] };
});

const getOnlineCourses = memoize((ids = [], userId, onlineCourses, users, ocUsers) => {
  const ocUsersArr = Object.values(ocUsers);
  const ocs = ids
    .map(id => {
      const oc = onlineCourses[id];
      return (
        oc && {
          ...oc,
          instructor: users[oc.public_instructor_id],
          current_user_online_course_user:
            ocUsers[oc.current_user_online_course_user_id] ||
            ocUsersArr.find(ocu => ocu.user_id === userId && ocu.online_course_id === oc.id),
        }
      );
    })
    .filter(Boolean);
  const smartPrep = ocs.find(oc => oc.course_type === 'class_pass');
  const others = ocs.filter(oc => oc.course_type === 'exam_prep');
  return [smartPrep, others];
});

const getStudySessionBundles = memoize((ids = [], bundles, bundleUsers, studySessions) =>
  ids
    .map(id => {
      const bundle = bundles[id];
      return (
        bundle && {
          ...bundle,
          current_user_study_session_bundle_user: bundleUsers[bundle.current_user_study_session_bundle_user_id],
          study_sessions: (bundle.study_session_ids || []).map(ssId => studySessions[ssId]).filter(Boolean),
        }
      );
    })
    .filter(Boolean),
);

const getStudySessions = memoize(
  (
    userId,
    courseId,
    subscriptionActive,
    ids = [],
    extendedAccessIds = [],
    studySessions,
    ssUsers,
    users,
    courseAssessments,
    courses,
  ) => {
    const ssUsersList = Object.values(ssUsers);
    return sortStudySessions(
      [...ids, ...extendedAccessIds]
        .map(id => {
          const ss = studySessions[id];
          const ssu =
            ss.purchase_type !== 'subscription' || subscriptionActive ?
              ssUsers[ss.current_user_study_session_user_id] ||
              ssUsersList.find(ssu => ssu.user_id === userId && ssu.study_session_id === ss.id)
            : undefined;
          if (getIsStudySessionExpired(ss, ssu)) return;
          const css = ss.course_study_sessions?.find(css => css.course_id === courseId);
          let assessmentId = css?.course_assessment_id;
          // if subscription study session and no current semester assessment, match to current semester assessment
          if (
            assessmentId &&
            ss.purchase_type === 'subscription' &&
            !courses[courseId].public_assessment_ids.includes(assessmentId)
          ) {
            assessmentId = courses[courseId].public_assessment_ids.find(
              aId => css.assessment_name && css.assessment_name === courseAssessments[aId]?.name,
            );
          }
          return {
            ...ss,
            current_user_study_session_user: ssu,
            non_student_study_session_users: ss.non_student_study_session_user_ids?.map(id => {
              const ssu = ssUsers[id];
              return {
                ...ssu,
                user: users[ssu.user_id],
              };
            }),
            assessment_id: assessmentId,
            assessment: courseAssessments[assessmentId], // for sorting.. :(
          };
        })
        .filter(Boolean),
    );
  },
);

const getAssessmentsWithContents = memoize((ids = [], assessments, classPass) => {
  return ids.map(aId => {
    const assessment = assessments[aId];
    const chapters = getAssessmentOnlineCourseChapters(assessment.items, classPass?.chapters);

    return {
      ...assessment,
      contents: !!chapters?.length && getOnlineCourseContentMap({ chapters: chapters }),
    };
  });
});

const getFlashCardDecks = memoize((ids = [], decks) => ids.map(id => decks[id]).filter(Boolean));

const getPracticeModeSectionResults = memoize((results, submissions, userId) => ({
  ...results,
  ...getUserSubmissionSectionResults(userId, submissions),
}));

const COURSE_FETCH_PARAMS = {
  fields: {
    course: [
      'active_course_instructors',
      'code',
      'country_code',
      'currency',
      'current_user_extended_access_study_sessions',
      'current_user_user_course',
      'current_user_class_rep_course',
      'featured',
      'has_class_rep',
      'high_priority',
      'name',
      'price',
      'public_assessments',
      'public_flash_card_decks',
      'public_online_courses',
      'public_study_sessions',
      'public_study_session_bundles',
      'sales_tax',
      'school',
      'semester',
      'slug',
      'stats',
      'stripe_product',
      'subject',
    ],
    online_course: [
      'activities',
      'chapters',
      'course',
      'course_assessment_id',
      'course_type',
      'currency',
      'current_user_online_course_user',
      'current_user_practice_mode_results',
      'current_user_token_purchases',
      'description',
      'fully_discountable',
      'in_course_settings',
      'instructor',
      'intro_video',
      'name',
      'order',
      'price',
      'regular_price',
      'packages_config',
      'practice_mode_section_question_counts',
      'public_instructor',
      'purchase_type',
      'sales_tax',
      'show_preview',
      'status',
      'subject',
      'user_access_level',
    ],
    user: ['name', 'avatar', 'slug', 'bio', 'degree', 'instructor_stats', 'intro_video', 'hide_from_public'],
    school: ['slug', 'name', 'short_name', 'type', 'country_code', 'region_code'],
    study_session: [
      'attachments',
      'booklet_excluded',
      'course_study_sessions',
      'courses',
      'cover_photo',
      'current_user_study_session_user',
      'custom_partner',
      'dates',
      'description',
      'disable_exam_prep_warning',
      'disable_zoom',
      'expires_at',
      'instructor_pending',
      'is_private',
      'location',
      'partner_program',
      'meeting_link',
      'non_student_study_session_users',
      'order',
      'partner',
      'price',
      'purchase_type',
      'recording_excluded',
      'regular_price',
      'solutions_excluded',
      'title',
      'type',
    ],
    study_session_user: [
      'role',
      'status',
      'study_session',
      'user',
      'zoom_info',
      'extended_access_date',
      'device_usages',
      'device_limit',
    ],
    study_session_bundle: [
      'current_user_study_session_bundle_user',
      'description',
      'hidden',
      'order',
      'price',
      'study_sessions',
      'title',
    ],
    study_session_bundle_user: ['status', 'created_at'],
    flash_card_deck: ['title', 'status', 'course'],
  },
  include: [
    'school',
    'active_course_instructors.instructor',
    'public_assessments',
    'public_online_courses.current_user_online_course_user',
    'public_online_courses.current_user_token_purchases',
    'public_online_courses.public_instructor',
    'public_flash_card_decks',
    'current_user_user_course',
    'current_user_class_rep_course',
    'public_study_sessions.current_user_study_session_user',
    'public_study_sessions.non_student_study_session_users.user',
    'current_user_extended_access_study_sessions.current_user_study_session_user',
    'current_user_extended_access_study_sessions.non_student_study_session_users.user',
    'public_study_session_bundles.current_user_study_session_bundle_user',
    'public_study_session_bundles.study_sessions',
    'stripe_product',
  ],
};
